import {Box, MenuItem, TextField, Typography} from '@mui/material';
import React, {Suspense, lazy, useEffect, useMemo, useState} from "react";
import {Link, useNavigate, useParams, useSearchParams} from "react-router-dom";
import axios from "axios";
import {API_ENDPOINT} from "../config";

import PhotoGallery from "../components/PhotoGallery";
import DescriereFormatata from "../components/DescriereFormatata";
import GrilaLucrari from "../components/GrilaLucrari";
import {lucrarePath, membruPath} from "../utils/urlPublic";
import {deschidereDiferita, formatData, formatDataOra} from "../components/dateExpozitie";
import {hideSplash} from "../splash";
import {TitluCuDistribuie} from "../components/DistribuieButton";
import LikeButton, {ActiuniCard} from "../components/LikeButton";
import {useAprecieriMele} from "../utils/aprecieri";

const HartaExpozitie = lazy(() => import("../components/HartaExpozitie"));

const ExpozitiePublicPage = () => {
    const {slug} = useParams();
    const navigate = useNavigate();
    const [expozitie, setExpozitie] = useState(null);
    const [loading, setLoading] = useState(true);
    // Ecranul de încărcare (src/splash.js) dispare când pagina are datele - fără „pâlpâire” la deschidere.
    useEffect(() => {
        if (!loading) hideSplash();
    }, [loading]);
    const [notFound, setNotFound] = useState(false);
    // Pasul 52: filtru pe artist pentru lucrarile expozitiei, pastrat in adresa (?artist=slug).
    const [searchParams, setSearchParams] = useSearchParams();
    // 54.5: starea „apreciat de mine” pentru expozitie si toate lucrarile ei, intr-o singura cerere.
    const apreciat = useAprecieriMele({
        expozitii: expozitie?.id ? [expozitie.id] : [],
        lucrari: (expozitie?.lucrari || []).filter((l) => l.likes !== null).map((l) => l.id),
    });
    const artistFiltru = searchParams.get('artist') || '';

    useEffect(() => {
        let mutat = false; // trecem pe adresa canonica: ramane „Se incarca” pana la cererea de acolo
        setLoading(true);
        setNotFound(false);
        axios.get(`${API_ENDPOINT}/public/expozitii/${slug}`)
            // 31.7: adresa veche (slug din istoric) -> API-ul da expozitia curenta; trecem pe adresa ei.
            .then((r) => {
                if (r.data.slug && r.data.slug !== slug) { mutat = true; navigate(`/expozitii/${encodeURIComponent(r.data.slug)}`, {replace: true}); }
                else setExpozitie(r.data);
            })
            .catch(() => setNotFound(true))
            .finally(() => { if (!mutat) setLoading(false); });
    }, [slug, navigate]);

    const online = expozitie?.online === true;
    const programata = expozitie?.programata === true && !!expozitie?.vernisaj;
    const artistiLucrari = useMemo(() => {
        const m = new Map();
        (expozitie?.lucrari || []).forEach((l) => {
            if (!l.artistSlug) return;
            const a = m.get(l.artistSlug) || {slug: l.artistSlug, nume: l.artist || l.artistSlug, n: 0};
            a.n += 1; m.set(l.artistSlug, a);
        });
        return [...m.values()].sort((a, b) => a.nume.localeCompare(b.nume, 'ro'));
    }, [expozitie]);
    const filtruValid = artistiLucrari.some((a) => a.slug === artistFiltru) ? artistFiltru : '';
    const lucrariAfisate = filtruValid ? expozitie.lucrari.filter((l) => l.artistSlug === filtruValid) : (expozitie?.lucrari || []);
    const schimbaArtist = (e) => {
        const p = new URLSearchParams(searchParams);
        if (e.target.value) p.set('artist', e.target.value); else p.delete('artist');
        // Filtrul nu e o pagina noua: pozitia ramane pe dropdown (exceptia pastreazaScroll din useRestaurareScroll).
        setSearchParams(p, {replace: true, state: {pastreazaScroll: true}});
    };
    return (
        <Box>
            <Box className="public-detail">
                {loading ? (
                    <Typography color="text.secondary">Se încarcă...</Typography>
                ) : notFound ? (
                    <Typography color="text.secondary">Expoziție negăsită.</Typography>
                ) : (
                    <>
                        <TitluCuDistribuie titlu={`${expozitie.numeExpozitie} — ArtaNFT`} text={`Expoziția ${expozitie.numeExpozitie}`}
                            like={<LikeButton tip="expozitii" id={expozitie.id} likes={expozitie.likes} apreciat={apreciat('expozitii', expozitie.id)} nume={expozitie.numeExpozitie} />}>
                            <Typography component="h1" variant="h4">{expozitie.numeExpozitie}</Typography>
                        </TitluCuDistribuie>
                        <Typography color="text.secondary" style={{marginBottom: '1.5rem'}}>
                            {/* 29.1: expozitia programata se anunta prin vernisaj (cu ora) */}
                            {programata ? <>
                                <strong>Vernisaj: {formatDataOra(expozitie.vernisaj)}</strong>
                                {deschidereDiferita(expozitie) && <><br />Deschidere pentru public: {formatDataOra(expozitie.dataStart)}</>}
                                {expozitie.dataEnd && <><br />Până la {formatData(expozitie.dataEnd)}</>}
                            </> : <>
                                {formatData(expozitie.dataStart) || 'Dată neanunțată'}
                                {expozitie.dataEnd && ` — ${formatData(expozitie.dataEnd)}`}
                            </>}
                            {online ? <><br />Online</> : <>
                                {expozitie.address && <><br />{expozitie.address}</>}
                                {expozitie.location && <><br />{expozitie.location}</>}
                            </>}
                        </Typography>

                        {/* Fara fotografii ale expozitiei sectiunea nu se mai afiseaza (in loc de „vor fi adaugate in curand”). */}
                        {expozitie.poze?.length > 0 && <PhotoGallery key={slug} photos={expozitie.poze} title={expozitie.numeExpozitie} />}

                        {!online && expozitie.latitudine != null && expozitie.longitudine != null && (
                            <Suspense fallback={null}>
                                <HartaExpozitie latitudine={expozitie.latitudine} longitudine={expozitie.longitudine}
                                                titlu={expozitie.numeExpozitie} />
                            </Suspense>
                        )}

                        <DescriereFormatata text={expozitie.descriere} />

                        {expozitie.artisti?.length > 0 && (
                            <Box style={{marginBottom: '2rem'}}>
                                <Typography component="h2" variant="h6" sx={{mb: 2}}>Artiști participanți prezenți pe platformă</Typography>
                                <ul>
                                    {expozitie.artisti.map((a) => (
                                        <li key={a.slug}>
                                            <Link to={membruPath(a.type, a.slug)}>{a.nume}</Link>
                                        </li>
                                    ))}
                                </ul>
                            </Box>
                        )}

                        {expozitie.lucrari?.length > 0 && (
                            <Box>
                                <Typography component="h2" variant="h6" sx={{mb: programata ? .5 : 2}}>{programata ? 'Lucrări care vor fi expuse' : 'Lucrări expuse'}</Typography>
                                {programata && <Typography color="text.secondary" sx={{mb: 2}}>Lucrările se dezvăluie la vernisaj.</Typography>}
                                {artistiLucrari.length > 1 && (
                                    <TextField select size="small" label="Artist" value={filtruValid} onChange={schimbaArtist}
                                               sx={{mb: 2, width: '100%', maxWidth: 360}}>
                                        <MenuItem value="">Toți artiștii ({expozitie.lucrari.length})</MenuItem>
                                        {artistiLucrari.map((a) => <MenuItem key={a.slug} value={a.slug}>{a.nume} ({a.n})</MenuItem>)}
                                    </TextField>
                                )}
                                <GrilaLucrari key={filtruValid} lucrari={lucrariAfisate} linkLucrare={(l) => lucrarePath(l.artistType, l.artistSlug, l.slug)}
                                    linkArtist={(l) => membruPath(l.artistType, l.artistSlug)} titluZoom={expozitie.numeExpozitie}
                                    actiuni={(l) => <ActiuniCard compact tip="lucrari" item={l} nume={l.nume} sx={{mx: -.75}}
                                        cale={lucrarePath(l.artistType, l.artistSlug, l.slug)} text={[l.nume, l.artist].filter(Boolean).join(' — ')}
                                        apreciat={apreciat('lucrari', l.id)} />} />
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

export default ExpozitiePublicPage;
